<script setup lang="ts">
import { CreateButton, SecondaryButton } from '@/components/Buttons'
import RadioButton from '@/components/Buttons/RadioButton.vue'
import InformationBaseCard from '@/components/Cards/InformationBaseCard.vue'
import { StackLayout } from '@/components/Layouts'
import { FullScreenLoader } from '@/components/LoadingSpinner'
import PermissionWrapper from '@/components/Permission/PermissionWrapper.vue'
import TypographyItem from '@/components/Typography/TypographyItem.vue'
import checkpointApi from '@/services/checkpoint.service'
import {
    ERdrChannel,
    ERdrChannelLabels,
    hasAvailableRdrChannel,
    RdrChannelKey
} from '@/types/enum/RdrChannelEnum'
import type { CheckpointAssociateErrorModel, CheckpointModel } from '@/types/models/CheckpointModel'
import type { AssociatedDeviceModel } from '@/types/models/DeviceModel'
import { ECheckpointsPerm } from '@/types/permissions/CheckpointsPermissionsEnum'
import moment from 'moment'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { DATE_FORMAT } from '../../../utils/dateUtils'
import CheckpointAssociatedDeviceCard from './components/CheckpointAssociatedDeviceCard.vue'
import CheckpointDeviceInput from './components/CheckpointDeviceInput.vue'

const { t } = useI18n()
const route = useRoute()
const eventId = computed(() => parseInt(route.params.event_id.toString()))

type Props = {
    checkpoint?: Partial<CheckpointModel>
}
const props = defineProps<Props>()
const emit = defineEmits<{ (e: 'refresh'): void }>()

const device = ref<AssociatedDeviceModel>()

const submitted = ref(false)
const isInEditMode = ref(false)
const isLoading = ref(false)

const associateError = ref<CheckpointAssociateErrorModel[]>()

const associateDevice = async (force?: boolean) => {
    if (isInEditMode.value) {
        submitted.value = true
        if (!device.value || !props.checkpoint?.id) return // TODO : Alerte
        isLoading.value = true

        checkpointApi
            .associateDeviceToCheckpoint(
                eventId.value,
                props.checkpoint.id,
                device.value?.device_id ?? device.value.id,
                selectedRdrChannel.value
                    ? { [RdrChannelKey]: selectedRdrChannel.value }
                    : undefined,
                force
            )
            .then(() => {
                emit('refresh')
                isInEditMode.value = false
                submitted.value = false
                device.value = undefined
            })
            .catch(err => (associateError.value = err.message))
            .finally(() => {
                if (force) associateError.value = undefined
                isLoading.value = false
            })
    }
    isInEditMode.value = true
}

const label = computed(() =>
    t(isInEditMode.value ? 'checkpoint.detail.associateDevice' : 'checkpoint.detail.associates')
)

const selectedRdrChannel = ref<ERdrChannel | undefined>()
const availableRdrChannels = computed(() =>
    device.value && props.checkpoint?.associates
        ? hasAvailableRdrChannel(device.value, props.checkpoint.associates)
        : undefined
)
</script>

<template>
    <FullScreenLoader :isLoading="isLoading" />
    <InformationBaseCard :label="$t('checkpoint.title.associateDeviceTitle')">
        <StackLayout direction="column" :gap="16" class="full-width">
            <StackLayout direction="column" :gap="8">
                <StackLayout v-if="checkpoint?.associates?.length" direction="column" :gap="8">
                    {{ $t('checkpoint.model.associates') }}
                    <StackLayout direction="row" :gap="8" wrap>
                        <CheckpointAssociatedDeviceCard
                            v-for="(device, key) in checkpoint?.associates"
                            :key="key"
                            :checkpoint="checkpoint"
                            @refresh="$emit('refresh')"
                            :device="device" />
                    </StackLayout>
                </StackLayout>
                <span v-else>{{ $t('checkpoint.detail.noDeviceAssociated') }}</span>
            </StackLayout>
            <PermissionWrapper :permission="ECheckpointsPerm.CHECKPOINTS_EDIT">
                <CheckpointDeviceInput
                    :isInEditMode="isInEditMode"
                    :associates="checkpoint?.associates ?? []"
                    @select="device = $event"
                    class="align-self-end" />
                <template v-if="availableRdrChannels && availableRdrChannels?.length">
                    <StackLayout direction="column" :gap="8">
                        <TypographyItem :label="$t('device.actions.rdrChannels.rdrChannelsText')" />
                        <RadioButton
                            :modelValue="selectedRdrChannel"
                            @change="selectedRdrChannel = $event"
                            :options="
                                availableRdrChannels?.map(value => ({
                                    name: $t(ERdrChannelLabels[value]?.label, { channel: value }),
                                    value
                                }))
                            " />
                    </StackLayout>
                </template>
                <template v-if="associateError && associateError?.length">
                    <StackLayout direction="column" :gap="8">
                        <TypographyItem
                            :label="$t('checkpoint.detail.alreadyAssociatedCheckpoints')" />
                        <TypographyItem
                            v-for="(item, index) in associateError"
                            :key="index"
                            :label="`${item.event} - ${item.checkpoint} - ${$t(
                                'date.from'
                            )} : ${moment(item.start_at).format(DATE_FORMAT)} ${$t(
                                'date.to'
                            )} : ${moment(item.start_at).format(DATE_FORMAT)}`" />
                    </StackLayout>
                </template>
                <StackLayout
                    direction="row"
                    :gap="8"
                    class="align-self-end"
                    isResponsiveSmallScreen>
                    <SecondaryButton
                        v-if="isInEditMode"
                        :label="$t('common.cancel')"
                        @click="isInEditMode = false" />
                    <CreateButton
                        v-if="associateError"
                        :label="$t('checkpoint.buttons.forceAssociate')"
                        @click="associateDevice(true)" />
                    <CreateButton
                        :loading="isLoading"
                        :disabled="!!associateError"
                        :label="label"
                        @click="associateDevice" />
                </StackLayout>
            </PermissionWrapper>
        </StackLayout>
    </InformationBaseCard>
</template>
